import "./header.style.scss";

import React, { useEffect, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { createStructuredSelector } from "reselect";

import DashboardListCustomModel from "../../components/dashboard-list-model/dashboard-list-custom-model.component";
import AiIcon from "../../components/Icon/Icons/AiIcon";
import { roles } from "../../constants/user";
import { adminRoles } from "../../helper/adminRoles.helpers";
import { trackEvent } from "../../hooks/useMixpanel";
import SelectWithSearchBox from "../../pages/townson-dashboard-list/components/client-data-upload-component/select-with-search-box";
import { show, update, updatePublicSettings } from "../../redux/ai/aiSlice";
import { isAiEnabled } from "../../redux/ai/utils";
import { setCompareToggle } from "../../redux/national-insights/national-insights.action";
import { selectCompareToggle } from "../../redux/national-insights/national-insights.selector";
import { setLocationHistory, setSelectedLocation } from "../../redux/street-insights/street-insights.action";
import { selectLocationHistory, selectSelectedLocation } from "../../redux/street-insights/street-insights.selector";
import { setThemeSetting } from "../../redux/theme/theme.action";
import { selectThemeSetting } from "../../redux/theme/theme.selector";
import { setToken } from "../../redux/token/token.action";
import { setCurrentUser } from "../../redux/user/user.action";
import { selectCurrentUser } from "../../redux/user/user.selector";
import { getPublicSystemSettings } from "../../services/administration.service";
import { getDashboard } from "../../services/dashboards/dashboard.service";

export const headerRef = React.createRef();

const dashboardRoutesWithAiPermissionCheck = [
	{ route: "/business-insights", type: "bi" },
	{ route: "/score-insights/", type: "si" },
];
function Header({
	user,
	theme,
	setThemeSetting,
	setCurrentUser,
	setToken,
	setCompareToggle,
	selectedLocation,
	setSelectedLocation,
	selectLocationHistory,
	setLocationHistory,
}) {
	const navigate = useNavigate();
	const location = useLocation();
	const { AUTHENTICATED_GUEST, AUTHENTICATED_USER } = roles;
	const [image, setImage] = useState();

	const showIcon = useSelector((state) => state.ai.showIcon);

	// Business Insights permissions come from the location slice
	const biRolePermissions = useSelector((state) => state.location.location?.rolePermissions);

	// Score Insights permissions come from the dashboard data slice
	const siPermissions = useSelector((state) => state.scoreInsightsDashboard.dashboardData?.permissions);

	const dispatch = useDispatch();

	const openAi = () => {
		dispatch(update(true));

		const askAiEvent = new CustomEvent("askAiQuestion");
		document.dispatchEvent(askAiEvent);
	};

	const [profileToggle, setProfileToggle] = useState(false);

	const [dashboardTrigger, setDashboardTrigger] = useState(false);
	const [settingTrigger, setSettingTrigger] = useState(false);

	const [resetSearchValue, setResetSearchValue] = useState(true);

	const defaultLogo = {
		light: "/" + import.meta.env.VITE_LIGHT_LOGO_PATH,
		dark: "/" + import.meta.env.VITE_DARK_LOGO_PATH,
	};

	const cresaLogo = {
		light: "/" + import.meta.env.VITE_CRESA_LIGHT_LOGO_PATH,
		dark: "/" + import.meta.env.VITE_CRESA_DARK_LOGO_PATH,
	};

	const setLogoImage = () => {
		if (user) {
			if (theme.theme_color === "dark-theme") {
				setImage(user.brand_dark_logo_full_path || cresaLogo.dark);
			} else {
				setImage(user.brand_logo_full_path || cresaLogo.light);
			}
		} else {
			setImage(theme.theme_color === "dark-theme" ? cresaLogo.dark : cresaLogo.light);
		}
	};

	useEffect(() => {
		setLogoImage();
		setSelectedLocation({});
	}, []);

	useEffect(() => {
		setLogoImage();
	}, [user, theme.theme_color]);

	useEffect(() => {
		if (location.pathname !== "/street-insights") {
			setResetSearchValue(!resetSearchValue);
			setSelectedLocation({});
		}
	}, [location.pathname]);

	const signOut = (e) => {
		e.preventDefault();
		setToken(null);
		setCurrentUser(null);
		navigate("/login");
	};

	const [userDashboard, setUserDashboard] = useState([]);
	const [errorLocationValidation, setErrorLocationValidation] = useState("");

	const getDashboardData = async () => {
		const data = [];
		await getDashboard().then(async (res) => {
			if (res?.code === 200) {
				res?.data.map((e, index) => {
					e?.dashboard?.dashboard_type.map((dash, index) => {
						data.push({
							...dash,
							dashboard_link: `${dash.dashboard_link}`,
						});
					});
				});
				setUserDashboard(data);
			}
		});
	};

	/** Get the list of dashboards for the dashboard dropdown on page load */
	useEffect(() => {
		getDashboardData();
	}, []);

	/** Clear error after a delay */
	useEffect(() => {
		if (errorLocationValidation !== "") {
			setTimeout(() => {
				setErrorLocationValidation("");
			}, 3000);
		}
	}, [errorLocationValidation]);

	const handleOnAddressChange = (value, index = 0) => {
		if (errorLocationValidation) {
			setErrorLocationValidation("");
		}

		if (value?.address?.postalCode) {
			setSelectedLocation(value);
			if (selectLocationHistory) {
				const tempData = [...selectLocationHistory];

				const checkUnique = tempData.filter((item) => {
					return item.id === value.id;
				});

				if (checkUnique.length === 0) {
					if (selectLocationHistory.length === 5) {
						setLocationHistory([...selectLocationHistory.slice(1, 5), value]);
					} else {
						setLocationHistory([...selectLocationHistory, value]);
					}
				}
			}

			trackEvent("Address Search", {
				"Search Address": value.address.freeformAddress,
				"Search Street": value.address.streetNumber + " " + value.address.streetName,
				"Search City": value.address.municipality,
				"Search State": value.address.addresscountrySubdivisionName,
				"Search Country": value.address.country,
				"Search Postal Code": value.address.postalCode,
				"Search County": value.address.countrySecondarySubdivision,
			});
			navigate("/street-insights");
		} else {
			setErrorLocationValidation("Postal code not found of this address");
			setSelectedLocation({});
		}
	};

	const userPage = () => {
		setDashboardTrigger(false);
		setSettingTrigger(false);
		navigate("/administration");
	};

	const { publicSettings } = useSelector((state) => state.ai);
	const biLocation = useSelector((state) => state.location);
	const siDashboard = useSelector((state) => state.scoreInsightsDashboard);

	useEffect(() => {
		const fetchPublicSettings = async () => {
			try {
				const response = await getPublicSystemSettings();
				dispatch(updatePublicSettings(response.data));
			} catch (error) {
				console.error("Failed to fetch public system settings", error);
			}
		};
		if (!publicSettings) {
			fetchPublicSettings();
		}
	}, [dispatch, publicSettings]);

	useEffect(() => {
		if (publicSettings) {
			const fullUrl = location.pathname + location.search;
			const aiEnabled = isAiEnabled({ ai: { publicSettings } }, fullUrl);

			// If the system, org, or route level check fails, hide the icon immediately
			if (!aiEnabled) {
				dispatch(show(false));
				return;
			}

			// For dashboard routes, also check whether this specific user was
			// explicitly granted AI access on this particular dashboard
			const matchedDashboardRoute = dashboardRoutesWithAiPermissionCheck.find(({ route }) =>
				location.pathname.startsWith(route),
			);

			if (matchedDashboardRoute) {
				const canUseAi =
					matchedDashboardRoute.type === "bi"
						? biRolePermissions?.can_use_ai === true
						: siPermissions?.can_use_ai === true;

				dispatch(show(canUseAi));
			} else {
				// All other approved routes only need the isAiEnabled() check
				dispatch(show(true));
			}
		}
	}, [dispatch, location, publicSettings, biRolePermissions, siPermissions]);
	return (
		<>
			<div
				className={`${dashboardTrigger || settingTrigger || profileToggle ? "show" : ""} overlay header-jsx`}
				onClick={() => {
					setDashboardTrigger(false);
					setSettingTrigger(false);
					setProfileToggle(false);
				}}
			/>

			<header className='header' id='SiteHeader' ref={headerRef}>
				<nav>
					<div className='container'>
						<div className='row'>
							<div className='col col-12 header-clm'>
								<div className='header-logo-wrapper'>
									<a href='/'>
										<img
											style={{ width: "120px" }}
											image-type={`${theme.theme_color}`}
											src={
												theme.theme_color === "dark-theme"
													? defaultLogo.dark
													: defaultLogo.light
											}
											alt={import.meta.env.VITE_APP_NAME}
										/>
									</a>
								</div>

								{user.roles && !user.roles.some((role) => role.name === "Guest") && (
									<>
										{(location.pathname === "/national-insights" || location.pathname === "/") && (
											<div className='header-search-wrapper'>
												{" "}
												<button
													id='compare-btn-id'
													className='btn outline rounded'
													onClick={() => {
														setCompareToggle(true);
													}}
												>
													Compare
												</button>
											</div>
										)}
										<div className='header-menu-wrapper ml-auto'>
											<ul>
												<li>
													<span
														className={`${
															!location.pathname.includes("user") &&
															!location.pathname.includes("policy") &&
															!location.pathname.includes("faq") &&
															!location.pathname.includes("administration")
																? "active"
																: ""
														}`}
														onClick={() => {
															setProfileToggle(false);
															setSettingTrigger(false);
															setDashboardTrigger(true);
														}}
													>
														Dashboards
													</span>
													<DashboardListCustomModel
														dashboardTrigger={dashboardTrigger}
														setDashboardTrigger={setDashboardTrigger}
														data={{
															name: "Dashboards",
															dashboard_type: userDashboard,
														}}
													/>
												</li>
												{user.role_can?.view_admin && (
													<li>
														<span
															className={`${
																location.pathname.includes("administration")
																	? "active"
																	: ""
															}`}
															onClick={() => userPage()}
														>
															Administration
														</span>
													</li>
												)}
												<li>
													<span
														className={`${
															location.pathname.includes("policy") ||
															location.pathname.includes("faq")
																? "active"
																: ""
														}`}
														onClick={() => {
															setDashboardTrigger(false);
															setProfileToggle(false);
															setSettingTrigger(true);
														}}
													>
														Resources
													</span>
													<DashboardListCustomModel
														dashboardTrigger={settingTrigger}
														setDashboardTrigger={setSettingTrigger}
														data={{
															name: "Resources",
															dashboard_type: [
																{
																	dashboard_link: "faq",
																	dasboard_name: "FAQs",
																},
																{
																	dashboard_link: "contact-us",
																	dasboard_name: "Contact US",
																},
															],
														}}
													/>
												</li>
											</ul>
										</div>
										<div className='header-user-wrapper'>
											{showIcon && !siDashboard.loading && (
												<div className={`ai-window pointer `}>
													<div onClick={openAi}>
														<AiIcon />
													</div>
												</div>
											)}
											<div className='profile'>
												<a
													href='#!'
													onClick={() => {
														setSettingTrigger(false);
														setDashboardTrigger(false);
														setProfileToggle(!profileToggle);
													}}
												>
													<img
														src={
															user.profile_pic
																? user.profile_pic
																: "/assets/images/profile-img.png"
														}
														alt=''
													/>
												</a>
											</div>
											<div className='d-flex align-items-center'>
												<img
													className='d-block'
													image-type={theme.theme_color}
													style={{
														marginLeft: "32px",
														width: "100%",
														maxWidth: "120px",
														maxHeight: "40px",
													}}
													src={`${image}`}
													alt={import.meta.env.VITE_APP_NAME}
												/>
											</div>
										</div>
									</>
								)}
							</div>
						</div>
					</div>
				</nav>

				<div className={`header-profile-box ${profileToggle ? "open" : ""}`}>
					<div className='header-profile-inner'>
						<div className='details header-profile-item'>
							<div className='profile-img'>
								<img
									src={user.profile_pic ? user.profile_pic : "/assets/images/profile-img.png"}
									alt=''
								/>
							</div>
							<p className='title'>{user ? `${user.first_name} ${user.last_name}` : "-"}</p>
							<label className='caption'>{user ? `${user.email}` : "-"}</label>
							{user && (
								<button
									className='btn outline rounded'
									onClick={(e) => {
										navigate({
											pathname: "/edit-user",
											search: `?id=${user.id}`,
											state: "manage-profile",
										});
										setProfileToggle(!profileToggle);
									}}
								>
									Manage your Profile
								</button>
							)}
						</div>
						<div className='profile-theme-toggle-wrap header-profile-item'>
							<div className='profile-theme-toggle'>
								<label className='body-2'>Theme</label>

								<div className='theme-toggle-switch'>
									<input
										type='checkbox'
										onChange={(e) => {
											if (e.target.checked) {
												setThemeSetting({ theme_color: "dark-theme" });
											} else {
												setThemeSetting({ theme_color: "light-theme" });
											}
										}}
										checked={theme.theme_color === "dark-theme"}
									/>
									<label />
									<img src='assets/images/switch-body-light.svg' className='light' alt='' />
									<img src='assets/images/switch-body-dark.svg' className='dark' alt='' />
								</div>
							</div>
						</div>
						<div className='profile-sign-out header-profile-item'>
							<button type='button' onClick={signOut} className='btn outline'>
								Sign Out of Account
							</button>
						</div>
						<div className='policy header-profile-item'>
							<ul>
								<li>
									<a href='/policy' className='caption'>
										Terms of Service
									</a>
								</li>
								<li>
									<a href='/faq' className='caption'>
										FAQS
									</a>
								</li>
							</ul>
						</div>
					</div>
				</div>
			</header>
		</>
	);
}

const mapStateToProps = createStructuredSelector({
	theme: selectThemeSetting,
	user: selectCurrentUser,
	compareToggle: selectCompareToggle,
	selectedLocation: selectSelectedLocation,
	selectLocationHistory,
});
const mapDispatchToProps = (dispatch) => ({
	setThemeSetting: (theme) => dispatch(setThemeSetting(theme)),
	setCurrentUser: (user) => dispatch(setCurrentUser(user)),
	setToken: (token) => dispatch(setToken(token)),
	setCompareToggle: (toggle) => dispatch(setCompareToggle(toggle)),
	setSelectedLocation: (location) => dispatch(setSelectedLocation(location)),
	setLocationHistory: (locations) => dispatch(setLocationHistory(locations)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Header);
